// Documents → Document Review: what an inbound document says, offered for a
// coordinator to confirm.
//
// A PPOT arrives by fax, gets filed to Drive, and today somebody opens the PDF
// and reads the plan period and the ordered hours off it. This shows those
// fields without opening anything, each with how sure the reading was and the
// words it came from, and lets a person accept, correct or reject each one.
//
// Only two fields write anywhere on acceptance (the ordering physician and
// their NPI). The rest are a record that a human read the document and agreed
// with what it says — which is the point: the facts become readable and
// searchable in CAERA instead of staying locked inside a scan.
import { useCallback, useEffect, useState } from 'react';
import {
  getClientExtractions, getReadableFaxes, acceptField, rejectField, extractFax,
  getFieldHistory, WRITES_TO, LOW_CONFIDENCE, CONFIDENCE_LABEL,
} from '../../lib/documentExtractions';
import Toast, { useToast } from '../Toast';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function Confidence({ value }) {
  const label = CONFIDENCE_LABEL(value);
  const colour = label === 'high' ? 'var(--ok)' : label === 'fair' ? 'var(--warn)' : 'var(--text3)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: colour }}>
      <span style={{ width: 28, height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: `${Math.round((value || 0) * 100)}%`, background: colour }} />
      </span>
      {label}
    </span>
  );
}

function Field({ row, profile, onDone, showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.extracted_value || '');
  const [busy, setBusy] = useState(false);

  const settled = row.status !== 'proposed';
  const notFound = !row.extracted_value;
  const writes = WRITES_TO[row.field_key];
  const [history, setHistory] = useState(null);
  const differs = row.current_value != null && row.extracted_value != null
    && String(row.current_value).trim() !== String(row.extracted_value).trim();

  const act = async (fn, msg) => {
    setBusy(true);
    const { error, wroteTo } = await fn();
    setBusy(false);
    if (error) { showToast(error.message, 'error'); return; }
    showToast(wroteTo ? `${msg} — client record updated` : msg);
    setEditing(false);
    onDone();
  };

  return (
    <div style={{
      display: 'grid', gap: 6, padding: '10px 12px', borderRadius: 8,
      background: settled ? 'transparent' : 'var(--bg2)',
      border: `1px solid ${settled ? 'var(--border)' : 'transparent'}`,
      opacity: row.status === 'rejected' ? 0.55 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 190 }}>{row.field_label}</span>
        {!notFound && <Confidence value={Number(row.confidence)} />}
        {writes && !settled && <span style={{ fontSize: 10, color: 'var(--text3)' }}>· accepting updates the client record, keeping the old value</span>}
      </div>

      {notFound ? (
        <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>Not stated on the document</div>
      ) : editing ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input className="form-input" style={{ flex: 1, minWidth: 200, fontSize: 13 }}
                 value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
          <button className="btn btn-primary" disabled={busy}
                  onClick={() => act(() => acceptField(row, draft, profile), 'Saved')}>Save</button>
          <button className="btn" disabled={busy} onClick={() => { setEditing(false); setDraft(row.extracted_value || ''); }}>Cancel</button>
        </div>
      ) : (
        <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>
          {row.status === 'edited' ? row.accepted_value : row.extracted_value}
          {row.status === 'edited' && (
            <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}> · corrected from “{row.extracted_value}”</span>
          )}
        </div>
      )}

      {differs && !settled && (
        <div style={{ fontSize: 11, color: 'var(--warn)' }}>
          CAERA currently has <b>{row.current_value}</b>
          {!writes && ' — these measure different things, neither overwrites the other'}
        </div>
      )}

      {row.source_quote && !settled && (
        <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>
          “{row.source_quote}”{row.source_page ? ` · page ${row.source_page}` : ''}
        </div>
      )}

      {settled ? (
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {row.status === 'rejected' ? 'Rejected' : row.status === 'edited' ? 'Corrected' : 'Accepted'}
            {row.reviewed_by_name ? ` by ${row.reviewed_by_name}` : ''} · {fmt(row.reviewed_at)}
            {row.replaced_value && (
              <> · replaced <span style={{ textDecoration: 'line-through' }}>{row.replaced_value.slice(0, 60)}{row.replaced_value.length > 60 ? '…' : ''}</span></>
            )}
          </div>
          {writes && (
            <button className="btn" style={{ justifySelf: 'start', fontSize: 11, padding: '2px 8px' }}
                    onClick={async () => {
                      if (history) { setHistory(null); return; }
                      const { data } = await getFieldHistory(row.client_id, row.field_key);
                      setHistory(data || []);
                    }}>
              {history ? 'Hide history' : 'History'}
            </button>
          )}
          {history?.length > 0 && (
            <div style={{ display: 'grid', gap: 3, paddingLeft: 10, borderLeft: '2px solid var(--border)' }}>
              {history.map((h, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--text3)' }}>
                  <b style={{ color: 'var(--text2)' }}>{h.effective_date || '—'}</b>
                  {' · '}{h.accepted_value}
                  {h.replaced_value && <> <span style={{ opacity: 0.7 }}>(was {h.replaced_value.slice(0, 40)}{h.replaced_value.length > 40 ? '…' : ''})</span></>}
                  {h.reviewed_by_name ? ` · ${h.reviewed_by_name}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : !notFound && !editing && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-primary" disabled={busy}
                  onClick={() => act(() => acceptField(row, null, profile), 'Accepted')}>
            {Number(row.confidence) < LOW_CONFIDENCE ? 'Accept anyway' : 'Accept'}
          </button>
          <button className="btn" disabled={busy} onClick={() => setEditing(true)}>Correct</button>
          <button className="btn" disabled={busy}
                  onClick={() => act(() => rejectField(row, profile), 'Rejected')}>Reject</button>
        </div>
      )}
    </div>
  );
}

// A form does not fill every box. The fields it did not state are still worth
// being able to see — they are the difference between "the document is silent"
// and "we never looked" — but they should not push what it did say off the
// screen, so they fold away behind a count.
function Section({ sec, profile, onDone, showToast }) {
  const [showBlank, setShowBlank] = useState(false);
  const found = sec.fields.filter((f) => f.extracted_value);
  const blank = sec.fields.filter((f) => !f.extracted_value);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>{sec.name}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{found.length} of {sec.fields.length} on the document</span>
      </div>
      {found.map((row) => <Field key={row.id} row={row} profile={profile} onDone={onDone} showToast={showToast} />)}
      {blank.length > 0 && (
        showBlank
          ? blank.map((row) => <Field key={row.id} row={row} profile={profile} onDone={onDone} showToast={showToast} />)
          : <button className="btn" style={{ justifySelf: 'start', fontSize: 11, padding: '3px 10px' }}
                    onClick={() => setShowBlank(true)}>
              {blank.length} field{blank.length === 1 ? '' : 's'} not stated on this document
            </button>
      )}
    </div>
  );
}

export default function ClientDocReviewTab({ client, profile }) {
  const [groups, setGroups] = useState(null);
  const [unread, setUnread] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const { toast, showToast, hideToast } = useToast();

  const load = useCallback(async () => {
    const [{ data: g }, { data: faxes }] = await Promise.all([
      getClientExtractions(client.id), getReadableFaxes(client.id),
    ]);
    setGroups(g || []);
    const done = new Set((g || []).map((x) => x.fax?.id));
    setUnread((faxes || []).filter((f) => !done.has(f.id)));
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const read = async (faxId) => {
    setBusyId(faxId);
    const { error } = await extractFax(faxId);
    setBusyId(null);
    if (error) { showToast(error.message, 'error'); return; }
    showToast('Document read');
    load();
  };

  if (groups === null) return <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>;

  const pending = groups.reduce((s, g) => s + g.pending, 0);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Toast message={toast?.message} type={toast?.type} onClose={hideToast} />
      <div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>What {client.first_name}'s documents say</div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          Fields read from inbound PPOTs, for you to confirm. Nothing reaches {client.first_name}'s record until you accept it.
          {pending > 0 && <> · <b style={{ color: 'var(--warn)' }}>{pending} waiting</b></>}
        </div>
      </div>

      {unread.length > 0 && (
        <div style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 8, border: '1px dashed var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>Not read yet</div>
          {unread.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, flex: 1, minWidth: 180 }}>
                {f.file_name || 'PPOT'} <span style={{ color: 'var(--text3)' }}>· {f.sender_name || 'unknown sender'} · {fmt(f.received_at)}</span>
              </span>
              {f.fields_extract_error && <span style={{ fontSize: 11, color: 'var(--err)' }}>{f.fields_extract_error}</span>}
              <button className="btn btn-primary" disabled={busyId === f.id} onClick={() => read(f.id)}>
                {busyId === f.id ? 'Reading…' : f.fields_extract_error ? 'Try again' : 'Read it'}
              </button>
            </div>
          ))}
        </div>
      )}

      {groups.length === 0 && unread.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>
          No readable documents on file yet. PPOTs received by fax appear here.
        </div>
      )}

      {groups.map((g) => (
        <div key={g.fax?.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg2)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 180 }}>
              PPOT <span style={{ color: 'var(--text3)', fontWeight: 400 }}>· {g.fax?.sender_name || 'unknown sender'} · received {fmt(g.fax?.received_at)}</span>
            </span>
            {g.pending > 0 && <span style={{ fontSize: 11, color: 'var(--warn)' }}>{g.pending} to review</span>}
            {g.fax?.drive_url && <a className="btn" href={g.fax.drive_url} target="_blank" rel="noreferrer">Open PDF</a>}
            <button className="btn" disabled={busyId === g.fax?.id} onClick={() => read(g.fax.id)}>
              {busyId === g.fax?.id ? 'Reading…' : 'Re-read'}
            </button>
          </div>
          <div style={{ display: 'grid', gap: 14, padding: 12 }}>
            {(g.sections || []).map((sec) => (
              <Section key={sec.name} sec={sec} profile={profile} onDone={load} showToast={showToast} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
